import type { PdSession, PdSessionAiResponse } from 'src/app/core/services/admin-session-api.service';
import { trimmedOrUndefined } from 'src/app/shared/utils/string.util';

export interface PdSessionAadhaarBack {
  address?: string | null;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  error?: string | null;
  captured_image?: string | null;
}

const ADDRESS_QUESTION_PATTERNS = [
  /where\s+do\s+you\s+live/i,
  /where\s+are\s+you\s+located/i,
  /current\s+address/i,
  /residential\s+address/i,
  /permanent\s+address/i,
  /correspondence\s+address/i,
  /place\s+of\s+residence/i,
  /city\s+(?:do\s+you\s+)?live/i,
  /your\s+address/i,
  /confirm\s+(?:your\s+)?address/i,
  /address\s+details/i,
];
const ADDRESS_QUESTION_EXCLUDE_RE =
  /email|e-mail|phone|mobile|loan\s+officer|agent|company|bank\s+branch/i;

function isAddressQuestion(question: string): boolean {
  const q = question.toLowerCase();
  if (ADDRESS_QUESTION_EXCLUDE_RE.test(q)) {
    return false;
  }
  return ADDRESS_QUESTION_PATTERNS.some((pattern) => pattern.test(q));
}

/** Parse `ai_response` when the API returns it as a JSON string. */
export function getSessionAiResponse(session: PdSession | null): PdSessionAiResponse | null {
  if (!session) return null;
  let raw = session.ai_response;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as PdSessionAiResponse;
    } catch {
      return null;
    }
  }
  return raw;
}

function appendUniqueAddressPart(parts: string[], value: string | null | undefined): void {
  const trimmed = value?.trim();
  if (!trimmed) return;
  const lower = trimmed.toLowerCase();
  if (!parts.some((p) => p.toLowerCase().includes(lower))) {
    parts.push(trimmed);
  }
}

function formatAadhaarBackLines(ab: PdSessionAadhaarBack): string | null {
  const parts: string[] = [];
  if (ab.address?.trim()) parts.push(ab.address.trim());
  appendUniqueAddressPart(parts, ab.district);
  appendUniqueAddressPart(parts, ab.state);
  if (ab.pincode?.trim()) parts.push(ab.pincode.trim());
  return parts.length ? parts.join(', ') : null;
}

function trimmedCapturedAddress(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function capturedAddressFromAi(ai: PdSessionAiResponse & {
  aadhaar_back?: PdSessionAadhaarBack | null;
  captured_address?: string | null;
}): string | null {
  const fromAadhaar = ai.aadhaar_back ? formatAadhaarBackLines(ai.aadhaar_back) : null;
  if (fromAadhaar) return fromAadhaar;
  return trimmedCapturedAddress(ai.captured_address);
}

function capturedAddressFromMetadata(meta: PdSession['metadata']): string | null {
  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta) as Record<string, unknown>;
      return trimmedCapturedAddress(parsed['captured_address']);
    } catch {
      return null;
    }
  }
  for (const key of ['captured_address', 'aadhaar_address'] as const) {
    const fromKey = trimmedCapturedAddress(meta?.[key]);
    if (fromKey) return fromKey;
  }
  return null;
}

/** OCR address from Aadhaar back, agent documents, or persisted metadata. */
export function formatCapturedAddress(session: PdSession | null): string | null {
  if (!session) return null;
  const ai = getSessionAiResponse(session) as PdSessionAiResponse & {
    aadhaar_back?: PdSessionAadhaarBack | null;
    captured_address?: string | null;
  };
  if (!ai) return null;

  const fromAi = capturedAddressFromAi(ai);
  if (fromAi) return fromAi;

  return capturedAddressFromMetadata(session.metadata);
}

export function capturedAddressError(session: PdSession | null): string | null {
  const ai = getSessionAiResponse(session) as PdSessionAiResponse & { aadhaar_back?: PdSessionAadhaarBack | null };
  return trimmedOrUndefined(ai?.aadhaar_back?.error) ?? null;
}

export type EnteredAddressParts = {
  /** Primary line for display (current or first address answer). */
  primary: string | null;
  current: string | null;
  permanent: string | null;
};

/** Spoken / typed address from PD interview Q&A stored in answer_scores. */
export function extractEnteredAddresses(session: PdSession | null): EnteredAddressParts {
  const empty: EnteredAddressParts = { primary: null, current: null, permanent: null };
  const ai = getSessionAiResponse(session);
  if (!ai?.answer_scores?.length) return empty;

  let current: string | null = null;
  let permanent: string | null = null;
  let fallback: string | null = null;

  for (const row of ai.answer_scores) {
    const q = row.question ?? '';
    const ans = row.answer?.trim();
    if (!ans || !isAddressQuestion(q)) continue;

    if (/permanent/i.test(q)) {
      permanent = ans;
    } else if (/current|residential|correspondence|where\s+do\s+you/i.test(q)) {
      current = ans;
    } else if (!fallback) {
      fallback = ans;
    }
  }

  if (!current) current = fallback;
  const primary = current ?? permanent ?? fallback;
  return { primary, current, permanent };
}

export function enteredAddressDisplay(parts: EnteredAddressParts): string | null {
  if (parts.current && parts.permanent && parts.current !== parts.permanent) {
    return `Current: ${parts.current}\nPermanent: ${parts.permanent}`;
  }
  return parts.primary;
}
